import type { PublicSource, SourceDocumentRef } from '@/api/sources/types';

/**
 * Etichette e formattazioni del catalogo fonti. Stesse tabelle dell'app
 * (`components/conversation/reference-documents/utils.ts`): se il backend
 * aggiunge una categoria o un cluster, aggiungerlo in entrambi i posti.
 */

const CATEGORY_LABELS: Record<string, string> = {
  adunanza_generale: 'Adunanza generale',
  adunanza_plenaria: 'Adunanza plenaria',
  approfondimento: 'Approfondimento',
  autorizzazione: 'Autorizzazione',
  ccnl: 'CCNL',
  circolare: 'Circolare',
  circolari: 'Circolare',
  concentrazione: 'Concentrazione',
  conclusione: 'Conclusione',
  decisione: 'Decisione',
  decreto: 'Decreto',
  decreto_del_capo_provvisorio_dello_stato: 'DCPS',
  decreto_legge: 'Decreto legge',
  decreto_legislativo: 'Decreto legislativo',
  decreto_legislativo_del_capo_provvisorio_dello_stato: 'DLCPS',
  decreto_legislativo_luogotenenziale: 'DLL',
  decreto_ministeriale: 'Decreto ministeriale',
  decreto_ministeriale_mef: 'Decreto ministeriale MEF',
  documento_interpretativo: 'Documento interpretativo OIC',
  chiarimento: 'Chiarimento OIC',
  comunicazione: 'Comunicazione OIC',
  emendamento: 'Emendamento OIC',
  principio: 'Principio contabile',
  redditi_sc: 'Istruzioni Redditi SC',
  redditi_sp: 'Istruzioni Redditi SP',
  redditi_pf: 'Istruzioni Redditi PF',
  redditi_enc: 'Istruzioni Redditi ENC',
  redditi_istruzioni_generali: 'Istruzioni generali Redditi',
  '730': 'Istruzioni 730',
  '770': 'Istruzioni 770',
  iva: 'Istruzioni IVA',
  isa: 'Istruzioni ISA',
  decreto_pcm: 'DPCM',
  decreto_pr: 'DPR',
  delibera: 'Delibera',
  deliberazione: 'Deliberazione',
  determinazione: 'Determinazione',
  documento: 'Documento',
  indagine_conoscitiva: 'Indagine conoscitiva',
  interpelli: 'Interpello',
  intesa_abuso: 'Intesa e abuso',
  legge: 'Legge',
  linea_guida: 'Linea guida',
  linee_guida: 'Linee guida',
  lodo: 'Lodo',
  messaggio: 'Messaggio',
  nota: 'Nota',
  nota_operativa: 'Nota operativa',
  ordinanza: 'Ordinanza',
  parere: 'Parere',
  pratica_scorretta: 'Pratica scorretta',
  provvedimenti: 'Provvedimento',
  provvedimento: 'Provvedimento',
  pubblicita_clausole: 'Pubblicità e clausole',
  regio_decreto: 'Regio decreto',
  regio_decreto_legge: 'Regio decreto legge',
  regio_decreto_legislativo: 'Regio decreto legislativo',
  regolamento: 'Regolamento',
  ricorso: 'Ricorso',
  risoluzioni: 'Risoluzione',
  segnalazione_parere: 'Segnalazione e parere',
  sentenza: 'Sentenza',
  sentenza_civile: 'Sentenza civile',
  sentenza_civile_lavoro: 'Sentenza lavoro',
  sentenza_penale: 'Sentenza penale',
};

const CLUSTER_LABELS: Record<string, string> = {
  ade: 'Agenzia delle Entrate',
  adm: 'Agenzia delle Dogane e dei Monopoli',
  agcm: 'AGCM',
  agid: 'AGID',
  ccnl: 'CCNL',
  eurlex: 'EUR-Lex',
  finanze_giurisprudenza: 'Giurisprudenza tributaria',
  garante_privacy: 'Garante Privacy',
  giustizia_amministrativa: 'Giustizia amministrativa',
  inail: 'INAIL',
  inps: 'INPS',
  italgiure_cassazione: 'Corte di Cassazione',
  italgiure_cassazione_civile: 'Corte di Cassazione',
  italgiure_cassazione_lavoro: 'Corte di Cassazione',
  italgiure_cassazione_penale: 'Corte di Cassazione',
  mit: 'Ministero delle Infrastrutture e dei Trasporti',
  modelli_ade: 'Istruzioni dei modelli dichiarativi',
  normattiva: 'Normattiva',
  oic: 'Principi contabili OIC',
  regioni: 'Bollettini ufficiali regionali',
};

/** Il cluster che raccoglie le venti fonti regionali. */
export const REGIONAL_CLUSTER_ID = 'regioni';

/**
 * Le regioni hanno un filtro tutto loro *in aggiunta* a «Normativa»: leggi e
 * regolamenti regionali sono normativa a tutti gli effetti, ma sono venti
 * fonti su trentasette e meritano di poter essere guardate da sole.
 */
export const REGIONS_TAG = 'Regioni';

/** Il numero di fonti regionali, per i conteggi prima che arrivi la risposta. */
const REGIONAL_SOURCES_COUNT = 20;

/** Ordine dei filtri: dalle norme alla prassi, poi giurisprudenza e contratti. */
const TAG_ORDER = [
  'Normativa',
  REGIONS_TAG,
  'Prassi',
  'Giurisprudenza',
  'Contratti collettivi',
  'Principi contabili',
];

export const sortTags = <T extends { id: string }>(chips: T[]): T[] =>
  [...chips].sort((a, b) => {
    const ia = TAG_ORDER.indexOf(a.id);
    const ib = TAG_ORDER.indexOf(b.id);
    return (ia === -1 ? TAG_ORDER.length : ia) - (ib === -1 ? TAG_ORDER.length : ib);
  });

/** I filtri sotto cui compare una fonte. */
export const sourceTags = (source: PublicSource): string[] =>
  source.id === REGIONAL_CLUSTER_ID ? [...(source.tags ?? []), REGIONS_TAG] : (source.tags ?? []);

/**
 * Quante fonti vale una riga: il cluster regionale ne vale venti, non una.
 * Prima della risposta live le sotto-fonti non ci sono ancora, quindi il
 * conteggio ricade sul numero noto.
 */
export const sourceWeight = (source: PublicSource): number =>
  source.sources?.length || (source.id === REGIONAL_CLUSTER_ID ? REGIONAL_SOURCES_COUNT : 1);

/**
 * Tinta del riquadro con la sigla, una per fonte. Sono le stesse dell'app:
 * chi passa dal sito al prodotto ritrova gli stessi colori sulle stesse fonti.
 * (Unica deroga alla regola dell'arancione: qui il colore è identità, non
 * gerarchia — non compete con le CTA.)
 */
const SOURCE_TINTS: Record<string, string> = {
  normattiva: 'bg-sky-50 text-sky-700',
  ade: 'bg-blue-50 text-blue-700',
  inail: 'bg-red-50 text-red-700',
  inps: 'bg-green-50 text-green-700',
  italgiure_cassazione_civile: 'bg-violet-50 text-violet-700',
  italgiure_cassazione_penale: 'bg-purple-50 text-purple-700',
  italgiure_cassazione_lavoro: 'bg-fuchsia-50 text-fuchsia-700',
  finanze_giurisprudenza: 'bg-pink-50 text-pink-700',
  ccnl: 'bg-amber-100 text-amber-700',
  adm: 'bg-orange-50 text-orange-700',
  agid: 'bg-emerald-50 text-emerald-700',
  agcm: 'bg-yellow-50 text-yellow-700',
  eurlex_normativa: 'bg-indigo-50 text-indigo-700',
  eurlex_giurisprudenza: 'bg-indigo-100 text-indigo-800',
  mit: 'bg-cyan-50 text-cyan-700',
  garante_privacy: 'bg-rose-50 text-rose-700',
  giustizia_amministrativa: 'bg-lime-50 text-lime-800',
  regioni: 'bg-teal-50 text-teal-700',
  decreti_mef: 'bg-sky-100 text-sky-800',
  modelli_ade: 'bg-blue-100 text-blue-800',
  oic: 'bg-violet-50 text-violet-700',
};

/** Le sotto-fonti regionali (`regioni_abruzzo`…) prendono la tinta del cluster. */
export const sourceTint = (id: string) =>
  SOURCE_TINTS[id] ??
  (id.startsWith(`${REGIONAL_CLUSTER_ID}_`) ? SOURCE_TINTS[REGIONAL_CLUSTER_ID] : undefined) ??
  'bg-mist-deep text-ink-muted';

export const categoryLabel = (category: string | null | undefined) =>
  (category && CATEGORY_LABELS[category]) || category || 'Documento';

/**
 * Codice del documento accanto alla data. I decreti MEF senza numero hanno come
 * codice il codice redazionale della Gazzetta Ufficiale (17A04710): da solo non
 * dice niente, quindi va qualificato. Stessa regola dell'app.
 */
const CODE_PREFIXES: Record<string, string> = {
  decreto_ministeriale_mef: 'Cod. G.U.',
};

export const documentCode = (category: string | null | undefined, code: string) =>
  category && CODE_PREFIXES[category] ? `${CODE_PREFIXES[category]} ${code}` : code;

export const clusterLabel = (cluster: string | null | undefined) =>
  (cluster && CLUSTER_LABELS[cluster]) || cluster || '';

/**
 * L'esito di una pronuncia arriva in snake_case dal riassunto
 * (`accoglimento_con_rinvio`): a schermo va letto come parole separate,
 * altrimenti il chip mostra l'underscore.
 */
export const outcomeLabel = (esito: string): string =>
  esito.replace(/_+/g, ' ').trim().replace(/\s+/g, ' ').toUpperCase();

const DATE_FORMAT: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
};

const isSameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();

/** "oggi alle 18:15" oppure "14 ago 2026 alle 09:00". */
export function formatSyncedAt(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const time = date.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  if (isSameDay(date, new Date())) return `oggi alle ${time}`;
  return `${date.toLocaleDateString('it-IT', DATE_FORMAT)} alle ${time}`;
}

/** "2 lug 2026", oppure "oggi" se il documento è di oggi. */
export function formatDocDate(date: string | null | undefined): string {
  if (!date) return '';
  const [year, month, day] = date.split('-').map(Number);
  if (!year || !month || !day) return '';
  const parsed = new Date(year, month - 1, day);
  if (isSameDay(parsed, new Date())) return 'oggi';
  return parsed.toLocaleDateString('it-IT', DATE_FORMAT);
}

/** Titolo del documento; se manca, lo ricostruiamo da cluster, numero e data. */
export function documentLabel(doc: SourceDocumentRef): string {
  if (doc.name) return doc.name;
  const parts = [clusterLabel(doc.cluster)];
  if (doc.number) parts.push(`n. ${doc.number}`);
  if (doc.date) parts.push(formatDocDate(doc.date));
  else if (doc.year) parts.push(String(doc.year));
  return parts.filter(Boolean).join(' ') || 'Documento';
}

/**
 * Link al documento originale. Normattiva marca alcuni link con `@originale`,
 * che apre l'atto com'era alla pubblicazione invece del testo vigente: va tolto.
 */
export function originalLink(link: string | null | undefined): string | null {
  return link ? link.replace(/@originale$/, '') : null;
}
